// Client-side mirrors of the worker's pure render helpers (composition-css.ts,
// composition-fonts.ts, script.ts, composition-layout.ts geometry). Kept identical
// so the DOM render matches the satori master. Guarded by the parity test.

import type { Fill, LayerBox, TextRun } from './types'

// --- script detection (mirror script.ts) ---------------------------------
const RE_HEBREW = /[֐-׿]/
const RE_ARABIC = /[؀-ۿݐ-ݿ]/
export const isHebrew = (t: string) => RE_HEBREW.test(t)
export const isArabic = (t: string) => RE_ARABIC.test(t)
export const isRtl = (t: string) => RE_HEBREW.test(t) || RE_ARABIC.test(t)

// --- fonts (mirror composition-fonts.ts) ---------------------------------
export function resolveFontFamily(logical: string, text: string): string {
  const arabic = isArabic(text)
  const rtl = isRtl(text)
  if (logical === 'Display' || logical === 'Serif') {
    if (arabic) return 'DisplayAr'
    if (rtl) return 'DisplayHe'
    return logical
  }
  if (logical === 'Body') {
    if (arabic) return 'BodyAr'
    if (rtl) return 'BodyHe'
    return 'Body'
  }
  return logical
}

// --- color math (mirror composition-layout.ts) ----------------------------
// The EXACT worker math: the editor's treatment toggles must pick the same pen
// the engine picks for the same accent (a threshold-only approximation chose the
// opposite text color on mid-luminance accents like pure red).

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.trim().replace(/^#/, '')
  if (h.length === 3)
    h = h
      .split('')
      .map(c => c + c)
      .join('')
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 0
  const lin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b)
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

export function readableTextColor(bg: string, light: string, dark: string): string {
  return contrastRatio(bg, light) >= contrastRatio(bg, dark) ? light : dark
}

// --- fills (mirror composition-css.ts) -----------------------------------
export function fillToCss(fill: Fill): string {
  switch (fill.kind) {
    case 'solid':
      return fill.color
    case 'gradient':
      return `linear-gradient(${fill.angleDeg ?? 160}deg, ${fill.from} 0%, ${fill.to} 100%)`
    case 'rgba': {
      if (fill.color.startsWith('radial:')) {
        const strength = parseFloat(fill.color.slice('radial:'.length)) || 0.45
        return `radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(0,0,0,${strength}) 100%)`
      }
      return fill.color
    }
    case 'linear': {
      const stops = fill.stops.map(s => `${s.color} ${s.atPct}%`).join(', ')
      return `linear-gradient(${fill.angleDeg}deg, ${stops})`
    }
  }
}

// --- geometry (mirror composition-layout.ts pxBox) -----------------------
export function pxBox(box: LayerBox, dims: { w: number; h: number }) {
  return {
    left: box.x * dims.w,
    top: box.y * dims.h,
    width: box.w * dims.w,
    height: box.h * dims.h,
    rotation: box.rotation ?? 0,
  }
}

// --- RTL visual ordering (mirror composition-layout.ts rtlVisualLine) -----
// eslint-disable-next-line no-irregular-whitespace -- intentional RTL unicode ranges (parity with worker)
const RTL_STRONG = /[֐-ࣿיִ-﷿ﹰ-﻿]/
export function rtlVisualLine(line: string): string {
  const chars = [...line]
  const n = chars.length
  if (n === 0) return line
  type Dir = 'R' | 'L'
  const strong: (Dir | null)[] = chars.map(c =>
    RTL_STRONG.test(c) ? 'R' : /[A-Za-z0-9]/.test(c) ? 'L' : null
  )
  for (let i = 0; i < n; i++) {
    if (strong[i]) continue
    const c = chars[i]!
    if (/\s/.test(c) || c === '-') continue
    if (strong[i - 1] === 'L' || strong[i + 1] === 'L') strong[i] = 'L'
  }
  const dir: Dir[] = new Array(n)
  for (let i = 0; i < n; i++) {
    if (strong[i]) {
      dir[i] = strong[i]!
      continue
    }
    let p = i - 1
    while (p >= 0 && !strong[p]) p--
    let q = i + 1
    while (q < n && !strong[q]) q++
    const left = p >= 0 ? strong[p]! : null
    const right = q < n ? strong[q]! : null
    dir[i] = left && right && left === right ? left : 'R'
  }
  const runs: { d: Dir; s: string }[] = []
  for (let i = 0; i < n;) {
    let j = i + 1
    while (j < n && dir[j] === dir[i]) j++
    runs.push({ d: dir[i]!, s: chars.slice(i, j).join('') })
    i = j
  }
  let out = ''
  for (let k = runs.length - 1; k >= 0; k--) {
    const r = runs[k]!
    out += r.d === 'R' ? [...r.s].reverse().join('') : r.s
  }
  return out
}

// Format a text run for DOM rendering: RTL Hebrew gets manual visual reordering
// (the browser DOES have bidi, but satori does not, so to MATCH the master we feed
// the same visually-ordered string and force LTR). Arabic is left in logical order
// (satori + browser both shape/reorder it natively).
export function formatRunText(
  text: string,
  uppercase?: boolean
): { text: string; forceLtr: boolean } {
  const t = uppercase ? text.toUpperCase() : text
  if (!isRtl(t)) return { text: t, forceLtr: false }
  if (isArabic(t)) return { text: t, forceLtr: false }
  return { text: rtlVisualLine(t), forceLtr: true }
}

// Group a text layer's runs into lines: a run starts a NEW line unless it is marked
// `inline` (then it flows after the previous run on the same line). The first run always
// starts a line. SHARED CONTRACT with the worker (composition-layout.ts) so both renderers
// lay text out identically. Legacy runs have no `inline` flag ⇒ one run per line ⇒ the prior
// column-of-blocks layout, unchanged.
export function groupRunsIntoLines<T extends Pick<TextRun, 'inline'>>(runs: T[]): T[][] {
  const lines: T[][] = []
  for (const run of runs) {
    if (run.inline && lines.length > 0) lines[lines.length - 1]!.push(run)
    else lines.push([run])
  }
  return lines
}
